import { EnvestnetYodleeLabel } from "@yodlee/common-components/dist/assets/logo";
import { Divider, Button, Typography, Form, Row, Icon, Statistic, Spin, Col, Input } from "antd";
import React, { useState, useCallback } from "react";
import { DataUtils } from "../../../utilities";
import OtpInput from "react-otp-input";
import { FormComponentProps } from "antd/lib/form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons";
import { faCheckCircle, faExclamationCircle as faExclamationCircleRegular } from "@fortawesome/pro-regular-svg-icons";

type SecondScreenIcon = "error" | "success";
type PageState = "email" | "otp" | "result";

interface UnsubscribeNotificationProps extends FormComponentProps { }

const UnsubscribeNotification: React.FC<UnsubscribeNotificationProps> = ({ form }) => {
    const [pageState, setPageState] = useState<PageState>("email");
    const [otpValidUntil, setOtpValidUntil] = useState<number>(0);
    const [resendDisabled, setResendDisabled] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [refId, setRefId] = useState<string>("");
    const [secondScreen, setSecondScreen] = useState<{ show: boolean; icon: SecondScreenIcon; message: string }>({
        show: false,
        icon: "success",
        message: "",
    });
    const [isVerifying, setIsVerifying] = useState<boolean>(false);
    const [email, setEmail] = useState<string>(""); 
    const [userEnteredEmail, setUserEnteredEmail] = useState<string>(""); 

    const sanitizeOtpValue = useCallback((value: string) => value.replace(/\D/g, "").slice(0, 6), []);

    const handleOtpChange = (value: string) => {
        const sanitizedValue = sanitizeOtpValue(value);
        form.setFields({
            otp: {
                value: sanitizedValue,
                errors: undefined,
            },
        });
        return sanitizedValue;
    };

    const getSecondScreenIconDefinition = (iconName: SecondScreenIcon) => {
        switch (iconName) {
            case "error":
                return faExclamationCircleRegular;
            case "success":
                return faCheckCircle;
            default:
                return null;
        }
    };

    const secondScreenIconDefinition = getSecondScreenIconDefinition(secondScreen.icon);
    const secondScreenIconClassName = secondScreen.icon === "error" ? "color-pink" : "color-green";

    const handleEmailSubmit = async () => {
        const emailValue: string = form.getFieldValue("email") || "";
        if (!emailValue || !emailValue.trim()) {
            form.setFields({
                email: {
                    value: emailValue,
                    errors: [<ErrorLabel label="Email is required" />],
                },
            });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue.trim())) {
            form.setFields({
                email: {
                    value: emailValue,
                    errors: [<ErrorLabel label="Please enter a valid email address" />],
                },
            });
            return;
        }

        form.setFields({
            email: {
                value: emailValue,
                errors: undefined,
            },
        });

        setIsLoading(true);
        setSecondScreen({ show: false, icon: "success", message: "" });

        const trimmedEmail = emailValue.trim();
        setUserEnteredEmail(trimmedEmail); 
        
        DataUtils.unsubscribeNotification().generateOtp(trimmedEmail).then((response) => {
            console.log(response);  
            console.log(response.otpValidUntil);
            setOtpValidUntil(response.otpValidUntil || 0);
            setResendDisabled(true);
            setRefId(response?.refId || "");
            setEmail(response?.user?.email || trimmedEmail); 
            setPageState("otp");
        }).catch((error) => {
            setSecondScreen({
                show: true,
                icon: "error",
                message: error?.response?.data?.message || "Unable to generate the code. Please try again.",
            });
            setPageState("result");
            console.error(error);
        }).finally(() => {
            setIsLoading(false);
        });
    };

    const handleContinue = async () => {
        const otpValue: string = form.getFieldValue("otp") || "";
        if (otpValue.length < 6) {
            form.setFields({
                otp: {
                    value: otpValue,
                    errors: [<ErrorLabel label="Required field"/>],
                },
            });
            return;
        }

        form.setFields({
            otp: {
                value: otpValue,
                errors: undefined,
            },
        });

        if (!refId) {
            form.setFields({
                otp: {
                    value: otpValue,
                    errors: [<ErrorLabel label="Reference id missing. Please request a new code." />],
                },
            });
            return;
        }

        if (!userEnteredEmail) {
            setSecondScreen({
                show: true,
                icon: "error",
                message: "Email is missing. Please start over.",
            });
            setPageState("result");
            return;
        }

        setIsVerifying(true);
        setSecondScreen({ show: false, icon: "success", message: "" });

        DataUtils.unsubscribeNotification().verifyOtp({
            email: userEnteredEmail, 
            otp: otpValue,
            refId,
        }).then((response) => {
            console.log("Verify OTP response:", response);
            setSecondScreen({
                show: true,
                icon: "success",
                message: "Unsubscribe request completed successfully.",
            });
            setPageState("result");
        }).catch((error) => {
            console.error('Verify OTP error:', error);
            const errorMessage = error?.response?.data?.message || "Unable to verify the code. Please try again.";
            if(!errorMessage.includes("You've exceeded the number of attempts")){
                form.setFields({
                    otp: {
                        value: otpValue,
                        errors: [<ErrorLabel label={errorMessage} />],
                    },
                });
            }
            else{
                setSecondScreen({
                    show: true,
                    icon: "error",
                    message: errorMessage,
                });
                setPageState("result");
            }
        }).finally(() => {
            setIsVerifying(false);
        });
    };

    const handleSendNewCode = () => {
        form.resetFields(["otp"]);
        setIsLoading(true);
        if (!userEnteredEmail) {
            setSecondScreen({
                show: true,
                icon: "error",
                message: "Email is missing. Please start over.",
            });
            setPageState("result");
            setIsLoading(false);
            return;
        }
        DataUtils.unsubscribeNotification().generateOtp(userEnteredEmail).then((response) => {
            console.log(response);
            setOtpValidUntil(response.otpValidUntil || 0);
            setResendDisabled(true);
            setRefId(response.refId || "");
            setEmail(response?.user?.email || userEnteredEmail); 
            setSecondScreen({ show: false, icon: "success", message: "" });
            setIsVerifying(false);
        }).catch((error) => {
            console.error(error);
            setSecondScreen({
                show: true,
                icon: "error",
                message: error?.response?.data?.message || "Unable to generate the code. Please try again.",
            });
        }).finally(() => {
            setIsLoading(false);
        });
    };

    const otpErrors = form.getFieldError("otp");
    const hasOtpError = Array.isArray(otpErrors) && otpErrors.length > 0;
    const otpHelp = hasOtpError ? otpErrors[0] : undefined;

    const ErrorLabel: React.FC<{ label: string }> = ({ label }) => {
        return (
          <>
            <Icon component={() => <FontAwesomeIcon icon={faExclamationCircle} />} className="color-pink" />
            <span style={{ paddingLeft: "4px" }}>{label}</span>
          </>
        );
      };

    const emailErrors = form.getFieldError("email");
    const hasEmailError = Array.isArray(emailErrors) && emailErrors.length > 0;
    const emailHelp = hasEmailError ? emailErrors[0] : undefined;

    const renderEmailPage = () => (
        <Row>
            <Row>
                <Typography.Paragraph style={{ textAlign: 'center', fontSize: '16px', marginBottom: '30px' }}>
                    To unsubscribe from notifications, please enter your email address.
                </Typography.Paragraph>
            </Row>
            <Row style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                <Form layout="vertical" colon={false} style={{ width: '320px', margin: '0 auto' }}>
                    <Form.Item
                        validateStatus={hasEmailError ? "error" : undefined}
                        help={emailHelp}
                    >
                        {form.getFieldDecorator('email', {
                            initialValue: "",
                            rules: [
                                { required: true, message: "Email is required" },
                                { type: 'email', message: "Please enter a valid email address" }
                            ],
                        })(
                            <Input
                                type="email"
                                placeholder="Enter your email address"
                                size="large"
                                disabled={isLoading}
                            />
                        )}
                    </Form.Item>
                </Form>
            </Row>
            <Row style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Button
                    type="primary"
                    onClick={handleEmailSubmit}
                    style={{width: '320px'}}
                    loading={isLoading}
                    disabled={isLoading}
                >
                    Continue
                </Button>
            </Row>
        </Row>
    );

    const renderOtpPage = () => (
        <Row>
            <Row>
                <Typography.Paragraph style={{ textAlign: 'center', fontSize: '16px', marginBottom: '30px' }}>
                    To protect your account, we need to verify your identity before processing your unsubscribe request.
                    <br />Please enter the 6 digit code that was just sent to <b>{email}</b>.
                </Typography.Paragraph>
            </Row>
            <Row style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                <Form layout="vertical" colon={false} style={{ maxWidth: '360px', margin: '0 auto' }}>
                    <Form.Item
                        label={null}
                        validateStatus={hasOtpError ? "error" : undefined}
                        help={otpHelp}
                    >
                        {form.getFieldDecorator('otp', {
                            initialValue: "",
                            getValueFromEvent: handleOtpChange,
                        })(
                            <OtpInput
                                numInputs={6}
                                inputType="number"
                                shouldAutoFocus
                                onChange={handleOtpChange}
                                inputStyle="ant-input"
                                renderSeparator={<span className="margin-left-xs" />}
                                renderInput={(props) =>   	<div className="ant-col ant-form-item-control-wrapper">
                                    <div className="ant-form-item-control">
                                        <span className="ant-form-item-children">
                                            <input {...props } style={{width: "50px", height:"56px", textAlign:"center"}}/>
                                            </span>
                                        </div>
                                    </div>}
                            />
                        )}
                        <p>
                            <Statistic.Countdown 
                                prefix={<Typography.Paragraph style={{ display: 'inline-block', marginBottom: 0 }}>Time Remaining:</Typography.Paragraph>} 
                                format='mm:ss' 
                                onFinish={() => { setResendDisabled(false); }} 
                                valueStyle={{ fontSize: "12px", color: "black" }} 
                                value={otpValidUntil}
                            />
                        </p>
                    </Form.Item>
                </Form>
            </Row>
            <Row style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Button
                    type="primary"
                    onClick={handleContinue}
                    style={{width: '320px'}}
                    loading={isVerifying}
                    disabled={isVerifying || hasOtpError}
                >
                    Continue
                </Button>
                <span className="margin-top-xs" />
                <Button
                    type="default"
                    onClick={handleSendNewCode}
                    style={{width: '320px'}}
                    disabled={resendDisabled || isLoading}
                    loading={isLoading}
                >
                    Send new code
                </Button>
            </Row>
        </Row>
    );

    const renderResultPage = () => {
        if (!secondScreen.show) {
            return null;
        }
        return (
            <Row style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {secondScreenIconDefinition ? (
                    <Icon
                        className={secondScreenIconClassName}
                        component={() => <FontAwesomeIcon size="3x" icon={secondScreenIconDefinition} />}
                    />
                ) : null}
                <span className="margin-top-xs" />
                <Typography.Paragraph style={{ textAlign: 'center', fontSize: '16px', marginBottom: '30px', maxWidth: '600px' }}>
                    {secondScreen.message}
                </Typography.Paragraph>
            </Row>
        );
    };

    return (
        <>
            <Row style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                <img src={EnvestnetYodleeLabel} height="35px" width="108px" className='main-logo' alt='Yodlee-label' />
            </Row>
            <Divider />
            {isLoading && pageState !== "otp" ?   
                <Row type="flex" style={{ height: "80vh" }} justify="center" align="middle">
                    <Col>
                        <Spin size="large" indicator={<Icon type="loading" />} />
                    </Col>
                </Row> : 
                pageState === "email" ? renderEmailPage() :
                pageState === "otp" ? renderOtpPage() :
                renderResultPage()
            }
        </>
    );
}

export default Form.create<UnsubscribeNotificationProps>({ name: "unsubscribeNotificationOtp" })(UnsubscribeNotification);